// Teacher Rules & Preferences Tab - SAT V4 Zone
import { DsButton, DsCard, DsIcon, DsTypography } from '@ice-web-app/designSystem';
import { Box, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';
import type { FC } from 'react';
import { useState } from 'react';
import type { Teacher, TeacherPreference } from '../../../../types';

const PREFERENCE_TYPES = [
  'No first period',
  'No last period',
  'Free period after lunch',
  'Consecutive periods for same class',
  'Maximum 3 periods in a row',
  'Preferred rooms',
  'No split days',
  'Morning preference',
  'Afternoon preference',
  'Other',
];

type RequestStatus = 'Pending' | 'Rejected' | 'Approved';

interface PreferenceRequest {
  id: string;
  preferenceType: string;
  dateRequest: string;
  status: RequestStatus;
  description: string;
}

const initialMockRequests: PreferenceRequest[] = [
  {
    id: 'req-1',
    preferenceType: 'No first period',
    dateRequest: '2024/12/15',
    status: 'Pending',
    description: 'I prefer not to have first period classes due to long commute.',
  },
  {
    id: 'req-2',
    preferenceType: 'Morning preference',
    dateRequest: '2024/12/10',
    status: 'Approved',
    description: 'I work better in the morning hours.',
  },
  {
    id: 'req-3',
    preferenceType: 'Consecutive periods for same class',
    dateRequest: '2024/12/05',
    status: 'Rejected',
    description: 'Would like consecutive periods for Grade 10 Math.',
  },
];

const getDefaultPreference = (): Omit<TeacherPreference, 'id'> => ({
  type: 'Other',
  description: '',
  priority: 2,
});

interface RulesPreferencesTabProps {
  teacher: Teacher;
  onUpdate?: (updates: Partial<Teacher>) => void;
}

const RulesPreferencesTab: FC<RulesPreferencesTabProps> = ({ teacher, onUpdate }) => {
  const preferences = teacher.preferences || [];
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [prefForm, setPrefForm] = useState<Omit<TeacherPreference, 'id'>>(getDefaultPreference());
  const [requests, setRequests] = useState<PreferenceRequest[]>(initialMockRequests);

  const handleOpenAdd = () => {
    setPrefForm(getDefaultPreference());
    setAddModalOpen(true);
  };

  const handleAddPreference = () => {
    if (prefForm.description) {
      const newPref: TeacherPreference = {
        ...prefForm,
        id: `pref-${Date.now()}`,
      };
      onUpdate?.({ preferences: [...preferences, newPref] });
      setAddModalOpen(false);
      setPrefForm(getDefaultPreference());
    }
  };

  const getStatusColor = (status: RequestStatus) => {
    switch (status) {
      case 'Pending':
        return 'warning';
      case 'Approved':
        return 'success';
      case 'Rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleRequestDescriptionChange = (id: string, description: string) => {
    setRequests(prev => prev.map(req =>
      req.id === id ? { ...req, description } : req
    ));
  };

  const handleSaveRequest = (_id: string) => {
    // In a real app, this would save to backend
  };

  const handleCancelRequest = (id: string) => {
    setRequests(prev => prev.filter(req => req.id !== id));
  };

  return (
    <Stack spacing={3}>
      {/* Preference Requests Cards */}
      <DsCard sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <DsTypography variant="h6" fontWeight={600}>
            Preference Requests
          </DsTypography>
          {onUpdate && (
            <DsButton variant="outlined" size="small" startIcon={<DsIcon icon="PLUS_CIRCLE" />} onClick={handleOpenAdd}>
              Add Preference
            </DsButton>
          )}
        </Stack>
        <Grid container spacing={2}>
          {requests.map((request) => (
            <Grid item xs={12} md={4} key={request.id}>
              <DsCard sx={{ p: 2, border: 1, borderColor: 'divider' }}>
                <Stack spacing={2}>
                  <DsTypography variant="subtitle1" fontWeight={600}>
                    Preference Request
                  </DsTypography>

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <DsTypography variant="body2" color="text.secondary">
                      Preference Type:
                    </DsTypography>
                    <DsTypography variant="body2">
                      {request.preferenceType}
                    </DsTypography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <DsTypography variant="body2" color="text.secondary">
                      Date Request:
                    </DsTypography>
                    <DsTypography variant="body2">
                      {request.dateRequest}
                    </DsTypography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <DsTypography variant="body2" color="text.secondary">
                      Status:
                    </DsTypography>
                    <Chip
                      label={request.status}
                      size="small"
                      color={getStatusColor(request.status) as 'warning' | 'success' | 'error' | 'default'}
                    />
                  </Stack>
                  <br />
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={2}
                    size="small"
                    value={request.description}
                    onChange={(e) => handleRequestDescriptionChange(request.id, e.target.value)}
                  />

                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <DsButton
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={() => handleCancelRequest(request.id)}
                    >
                      Delete
                    </DsButton>
                    <DsButton
                      variant="contained"
                      size="small"
                      onClick={() => handleSaveRequest(request.id)}
                    >
                      Edit
                    </DsButton>
                  </Stack>
                </Stack>
              </DsCard>
            </Grid>
          ))}
          {requests.length === 0 && (
            <Grid item xs={12}>
              <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'background.default', borderRadius: 2 }}>
                <DsTypography color="text.secondary">
                  No preference requests.
                </DsTypography>
              </Box>
            </Grid>
          )}
        </Grid>
      </DsCard>

      {/* Common Preference Types */}
      <DsCard sx={{ p: 3 }}>
        <DsTypography variant="h6" fontWeight={600} gutterBottom>
          Common Preference Types
        </DsTypography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {[
            'No first period',
            'No last period',
            'Free period after lunch',
            'Consecutive periods for same class',
            'Maximum 3 periods in a row',
            'Preferred rooms',
            'No split days',
            'Morning preference',
            'Afternoon preference',
          ].map((type) => (
            <Chip
              key={type}
              label={type}
              variant="outlined"
              onClick={onUpdate ? () => { } : undefined}
              sx={{ cursor: onUpdate ? 'pointer' : 'default' }}
            />
          ))}
        </Stack>
      </DsCard>

      {/* Add Preference Modal */}
      <Dialog open={addModalOpen} onClose={() => setAddModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Preference</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Preference Type</InputLabel>
              <Select
                value={prefForm.type}
                label="Preference Type"
                onChange={(e) => setPrefForm({ ...prefForm, type: e.target.value })}
              >
                {PREFERENCE_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={prefForm.description}
              onChange={(e) => setPrefForm({ ...prefForm, description: e.target.value })}
              placeholder="Describe the preference in detail..."
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <DsButton variant="outlined" onClick={() => setAddModalOpen(false)}>Cancel</DsButton>
          <DsButton variant="contained" onClick={handleAddPreference} disabled={!prefForm.description}>
            Add Preference
          </DsButton>
        </DialogActions>
      </Dialog>

    </Stack>
  );
};

export default RulesPreferencesTab;

